import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock3,
  RefreshCw,
  Search,
} from 'lucide-react';
import { AdminLayout } from '../components/AdminLayout';
import {
  getExtractionBatchAnalytics,
  type ExtractionBatchAnalyticsResponse,
  type ExtractionBatchDiagnostic,
  type ExtractionBatchItem,
  type ExtractionBatchRow,
} from '../services/adminApi';
import {
  Banner,
  ButtonRow,
  Card,
  CardDescription,
  CardTitle,
  Metric,
  PrimaryButton,
  RawJson,
  SecondaryButton,
  StatGrid,
} from '../components/admin/AdminUI';
import { borderRadius, colors, shadows, spacing, typography } from '../styles/theme';
import { toAbsoluteUrl } from '../utils/externalUrl';

const PAGE_SIZE = 25;
const ITEM_PAGE_SIZE = 100;
const STATUS_OPTIONS = [
  'building', 'submitted', 'validating', 'in_progress', 'finalizing', 'cancelling',
  'completed', 'imported', 'completed_with_errors', 'cancelled', 'expired', 'failed',
];

const FilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: ${spacing[3]};
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: ${spacing[1]};
  min-width: 150px;
  font-size: ${typography.fontSizes.xs};
  font-weight: ${typography.fontWeights.semibold};
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const Input = styled.input`
  min-height: 38px;
  padding: ${spacing[2]} ${spacing[3]};
  border: 1px solid ${colors.border.normal};
  border-radius: ${borderRadius.md};
  background: ${colors.white};
  color: ${colors.text.primary};
  font: inherit;
`;

const Select = styled.select`
  min-height: 38px;
  padding: ${spacing[2]} ${spacing[3]};
  border: 1px solid ${colors.border.normal};
  border-radius: ${borderRadius.md};
  background: ${colors.white};
  color: ${colors.text.primary};
`;

const Toggle = styled.label`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[2]};
  min-height: 38px;
  padding: 0 ${spacing[2]};
  font-size: ${typography.fontSizes.sm};
  color: ${colors.text.primary};
  white-space: nowrap;
`;

const TableWrap = styled.div`
  overflow-x: auto;
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.lg};
  box-shadow: ${shadows.sm};
`;

const Table = styled.table`
  width: 100%;
  min-width: 980px;
  border-collapse: collapse;
  background: ${colors.white};
  font-size: ${typography.fontSizes.sm};
`;

const Th = styled.th<{ align?: string }>`
  padding: ${spacing[2]};
  text-align: ${p => p.align || 'left'};
  color: ${colors.text.secondary};
  background: ${colors.gray[50]};
  border-bottom: 1px solid ${colors.border.light};
  font-size: 11px;
  text-transform: uppercase;
  white-space: nowrap;
`;

const Td = styled.td.withConfig({
  shouldForwardProp: prop => prop !== 'wrap',
})<{ align?: string; wrap?: boolean }>`
  padding: ${spacing[2]};
  text-align: ${p => p.align || 'left'};
  color: ${colors.text.primary};
  border-bottom: 1px solid ${colors.border.light};
  vertical-align: top;
  white-space: ${p => p.wrap ? 'normal' : 'nowrap'};
  max-width: ${p => p.wrap ? '380px' : 'none'};
  overflow-wrap: anywhere;
`;

const SelectableRow = styled.tr.withConfig({
  shouldForwardProp: prop => prop !== 'selected' && prop !== 'issue',
})<{ selected?: boolean; issue?: boolean }>`
  cursor: pointer;
  background: ${p => p.selected
    ? colors.primary[50]
    : p.issue ? colors.warning[50] : colors.white};

  &:hover {
    background: ${p => p.selected ? colors.primary[100] : colors.gray[50]};
  }
`;

const Badge = styled.span<{ tone: 'success' | 'error' | 'warning' | 'neutral' }>`
  display: inline-block;
  padding: 2px ${spacing[2]};
  border-radius: ${borderRadius.md};
  font-size: 11px;
  font-weight: ${typography.fontWeights.semibold};
  background: ${p => p.tone === 'success' ? colors.success[50]
    : p.tone === 'error' ? colors.error[50]
      : p.tone === 'warning' ? colors.warning[50] : colors.gray[100]};
  color: ${p => p.tone === 'success' ? colors.success[700]
    : p.tone === 'error' ? colors.error[700]
      : p.tone === 'warning' ? colors.warning[700] : colors.text.secondary};
`;

const Mono = styled.code`
  font-size: ${typography.fontSizes.xs};
  color: ${colors.text.secondary};
`;

const MetadataGrid = styled.dl`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
  gap: ${spacing[3]} ${spacing[5]};
  margin: ${spacing[4]} 0 0;
`;

const Metadata = styled.div`
  min-width: 0;

  dt {
    color: ${colors.text.tertiary};
    font-size: ${typography.fontSizes.xs};
    text-transform: uppercase;
    margin-bottom: 2px;
  }

  dd {
    margin: 0;
    color: ${colors.text.primary};
    overflow-wrap: anywhere;
  }
`;

const IssueList = styled.div`
  display: grid;
  gap: ${spacing[2]};
  margin-top: ${spacing[3]};
`;

const Issue = styled.div<{ severity: 'error' | 'warning' }>`
  display: grid;
  grid-template-columns: 20px minmax(0, 1fr);
  gap: ${spacing[2]};
  padding: ${spacing[3]};
  background: ${p => p.severity === 'error' ? colors.error[50] : colors.warning[50]};
  color: ${p => p.severity === 'error' ? colors.error[800] : colors.warning[800]};
  border-left: 3px solid ${p => p.severity === 'error' ? colors.error[500] : colors.warning[500]};
  font-size: ${typography.fontSizes.sm};
`;

const Pager = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing[3]};
  flex-wrap: wrap;
  margin-top: ${spacing[3]};
  color: ${colors.text.secondary};
  font-size: ${typography.fontSizes.sm};
`;

function duration(ms: number | null | undefined): string {
  if (ms == null) return '—';
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes}m ${seconds % 60}s`;
}

function bytes(value: number | null | undefined): string {
  if (value == null) return '—';
  if (value < 1024) return `${value} B`;
  if (value < 1024 ** 2) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / 1024 ** 2).toFixed(2)} MB`;
}

function money(value: number | null | undefined): string {
  if (value == null) return '—';
  if (value === 0) return '$0.00';
  if (value < 0.001) return '<$0.001';
  return `$${value.toFixed(4)}`;
}

function percent(value: number | null | undefined): string {
  return value == null ? '—' : `${(value * 100).toFixed(1)}%`;
}

function dateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
}

function shortId(value: string | null | undefined): string {
  if (!value) return '—';
  return value.length > 18 ? `${value.slice(0, 10)}…${value.slice(-6)}` : value;
}

function tone(status: string): 'success' | 'error' | 'warning' | 'neutral' {
  if (status === 'imported' || status === 'completed') return 'success';
  if (status === 'failed' || status === 'expired' || status === 'completed_with_errors') return 'error';
  if (status === 'cancelled' || status.includes('fallback') || status === 'late_ignored') return 'warning';
  return 'neutral';
}

function Diagnostics({ values }: { values: ExtractionBatchDiagnostic[] }) {
  if (values.length === 0) {
    return <Banner tone="success"><CheckCircle2 size={16} /> No durable-state issues detected.</Banner>;
  }
  return (
    <IssueList aria-label="Diagnostics">
      {values.map((issue, index) => (
        <Issue key={`${issue.code}-${index}`} severity={issue.severity}>
          <AlertTriangle size={17} />
          <div><strong>{issue.code.replaceAll('_', ' ')}</strong><br />{issue.message}</div>
        </Issue>
      ))}
    </IssueList>
  );
}

function BatchMetadata({ batch }: { batch: ExtractionBatchRow }) {
  return (
    <MetadataGrid>
      {[
        ['Internal batch ID', batch.id],
        ['Provider batch ID', batch.providerBatchId],
        ['Gate batch ID', batch.gateBatchId],
        ['Deployment', batch.deployment],
        ['Model version', `${batch.model || '—'} / ${batch.modelVersion || '—'}`],
        ['SKU / API', `${batch.deploymentSku || '—'} / ${batch.apiVersion || '—'}`],
        ['Input file', batch.inputFileId],
        ['Output file', batch.outputFileId],
        ['Error file', batch.errorFileId],
        ['Input hash', batch.inputHash],
        ['Created', dateTime(batch.timing.createdAt)],
        ['Submitted', dateTime(batch.timing.submittedAt)],
        ['Provider complete', dateTime(batch.timing.providerCompletedAt)],
        ['Imported', dateTime(batch.timing.importedAt)],
        ['Fallback deadline', dateTime(batch.timing.fallbackAt)],
        ['Outer deadline', dateTime(batch.timing.outerDeadlineAt)],
      ].map(([label, value]) => (
        <Metadata key={label}>
          <dt>{label}</dt>
          <dd><Mono>{value || '—'}</Mono></dd>
        </Metadata>
      ))}
    </MetadataGrid>
  );
}

function ItemSource({ item }: { item: ExtractionBatchItem }) {
  const safeSourceUrl = toAbsoluteUrl(item.sourceUrl);
  return (
    <>
      <strong>{item.entityName || `Source ${item.eventSourceId ?? '—'}`}</strong><br />
      {safeSourceUrl ? (
        <a href={safeSourceUrl} target="_blank" rel="noreferrer">{safeSourceUrl}</a>
      ) : '—'}<br />
      <Mono>gate {shortId(item.gateRunId)} · workflow {item.workflowMetadataId ?? '—'}</Mono>
    </>
  );
}

function itemIssueText(item: ExtractionBatchItem): string {
  const messages = [
    item.error && `${item.error.code || 'item_error'}: ${item.error.message || 'No error message recorded.'}`,
    ...item.diagnostics.map(issue => issue.message),
  ].filter((value): value is string => Boolean(value));
  return Array.from(new Set(messages)).join(' ') || '—';
}

function ItemTable({ items }: { items: ExtractionBatchItem[] }) {
  return (
    <TableWrap role="region" aria-label="Scrollable Batch items" tabIndex={0}>
      <Table aria-label="Batch items">
        <thead>
          <tr>
            <Th>Status</Th><Th>Source</Th><Th>Provider</Th><Th align="right">Tokens</Th>
            <Th align="right">Cost</Th><Th>Timing</Th><Th>Issue</Th>
          </tr>
        </thead>
        <tbody>
          {items.map(item => (
            <tr key={item.id}>
              <Td>
                <Badge tone={tone(item.status)}>{item.status}</Badge><br />
                <Mono>{item.workflowAttemptCount ?? 0} workflow attempt{item.workflowAttemptCount === 1 ? '' : 's'}</Mono>
              </Td>
              <Td wrap><ItemSource item={item} /></Td>
              <Td>
                {item.providerResultStatus || '—'} / HTTP {item.responseStatusCode ?? '—'}<br />
                <Mono>{shortId(item.providerRequestId)}</Mono>
              </Td>
              <Td align="right">
                {item.usage.totalTokens.toLocaleString()}<br />
                <Mono>{item.usage.inputTokens.toLocaleString()} in / {item.usage.outputTokens.toLocaleString()} out</Mono>
              </Td>
              <Td align="right">{money(item.estimatedBatchCostUsd)}</Td>
              <Td>
                result {dateTime(item.timing.resultReceivedAt)}<br />
                <Mono>fallback {dateTime(item.timing.fallbackQueuedAt)}</Mono>
              </Td>
              <Td wrap>{itemIssueText(item)}</Td>
            </tr>
          ))}
          {items.length === 0 && <tr><Td colSpan={7}>No items found for this page.</Td></tr>}
        </tbody>
      </Table>
    </TableWrap>
  );
}

export default function AdminExtractionBatchesPage() {
  const [days, setDays] = useState(90);
  const [status, setStatus] = useState('');
  const [issuesOnly, setIssuesOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [itemPage, setItemPage] = useState(1);
  const [selectedBatchId, setSelectedBatchId] = useState<string | null>(null);
  const [data, setData] = useState<ExtractionBatchAnalyticsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  const load = useCallback(async (next: {
    page?: number;
    itemPage?: number;
    batchId?: string | null;
  } = {}) => {
    const nextPage = next.page ?? page;
    const nextItemPage = next.itemPage ?? itemPage;
    const batchId = next.batchId === undefined ? selectedBatchId : next.batchId;
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const response = await getExtractionBatchAnalytics({
        days,
        page: nextPage,
        pageSize: PAGE_SIZE,
        status: status || undefined,
        issuesOnly,
        search: search.trim() || undefined,
        batchId: batchId || undefined,
        itemPage: nextItemPage,
        itemPageSize: ITEM_PAGE_SIZE,
      });
      if (requestId !== requestIdRef.current) return;
      setData(response);
      setPage(nextPage);
      setItemPage(nextItemPage);
      setSelectedBatchId(batchId || null);
    } catch (loadError: any) {
      if (requestId === requestIdRef.current) {
        setError(loadError?.message || 'Could not load extraction Batch analytics');
      }
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [days, itemPage, issuesOnly, page, search, selectedBatchId, status]);

  useEffect(() => {
    load({ page: 1, itemPage: 1, batchId: null });
    // Initial fetch only; filters are applied explicitly to avoid query storms.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const summary = data?.summary;
  const selected = data?.selectedBatch;
  const allDiagnostics = useMemo(() => {
    const byCode = new Map<string, ExtractionBatchDiagnostic>();
    for (const value of [...(data?.diagnostics || []), ...(selected?.diagnostics || [])]) {
      if (!byCode.has(value.code)) byCode.set(value.code, value);
    }
    return Array.from(byCode.values());
  }, [data?.diagnostics, selected?.diagnostics]);

  const applyFilters = () => {
    setSelectedBatchId(null);
    setPage(1);
    setItemPage(1);
    load({ page: 1, itemPage: 1, batchId: null });
  };

  return (
    <AdminLayout
      currentPage="extractionBatches"
      pageTitle="Extraction AI Batches"
      pageDescription="Review Batch timing, payload size, token cost, durable state, fallbacks, and provider or workflow errors."
    >
      {error && <Banner tone="error">{error}</Banner>}
      {data && !data.schemaReady && (
        <Banner tone="error">Batch schema is not installed in this database. Runtime remains unavailable.</Banner>
      )}
      {data && (
        <Banner tone={data.rollout.enabled ? 'warning' : 'info'}>
          Runtime mode: <strong>{data.rollout.mode}</strong> · canary {data.rollout.canaryPercent}% ·
          deployment {data.rollout.deployment || 'not configured'}
        </Banner>
      )}

      {process.env.NODE_ENV === 'development' && (
        <ButtonRow style={{ marginBottom: spacing[5] }}>
          <SecondaryButton as="a" href="/admin/batches/verify">
            <CheckCircle2 size={16} /> Verify extraction quality
          </SecondaryButton>
        </ButtonRow>
      )}

      {summary && (
        <StatGrid>
          <Metric label="Batches" value={summary.totalBatches.toLocaleString()} />
          <Metric label="Items" value={summary.totalItems.toLocaleString()} />
          <Metric label="Issue batches" value={summary.issueBatches.toLocaleString()}
            tone={summary.issueBatches > 0 ? 'error' : 'success'} />
          <Metric label="Fallbacks" value={summary.fallbackItems.toLocaleString()}
            tone={summary.fallbackItems > 0 ? 'warning' : 'neutral'} />
          <Metric label="Input size" value={bytes(summary.inputBytes)} />
          <Metric label="Provider p50" value={duration(summary.providerDuration.p50Ms)} />
          <Metric label="Provider p95" value={duration(summary.providerDuration.p95Ms)} />
          <Metric label="Batch cost" value={money(summary.cost.batchUsd)} />
          <Metric label="Equivalent Standard" value={money(summary.cost.standardEquivalentUsd)} />
          <Metric label="Est. token savings" value={percent(summary.cost.savingsPct)}
            tone={summary.cost.savingsPct != null && summary.cost.savingsPct >= 0.3 ? 'success' : 'warning'} />
        </StatGrid>
      )}

      <Card>
        <CardTitle>Find batches</CardTitle>
        <CardDescription>Summary metrics cover the selected time window. Status, issue, and search filters apply to the table.</CardDescription>
        <FilterBar>
          <Field>Window
            <Select aria-label="Window" value={days} onChange={event => setDays(Number(event.target.value))}>
              <option value={7}>Last 7 days</option><option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option><option value={365}>Last year</option>
            </Select>
          </Field>
          <Field>Status
            <Select aria-label="Status" value={status} onChange={event => setStatus(event.target.value)}>
              <option value="">All statuses</option>
              {STATUS_OPTIONS.map(option => <option key={option} value={option}>{option}</option>)}
            </Select>
          </Field>
          <Field style={{ flex: '1 1 260px' }}>ID, model, deployment
            <Input aria-label="ID, model, deployment" value={search}
              onChange={event => setSearch(event.target.value)} maxLength={100}
              title="Maximum 100 characters" placeholder="Search durable or provider ID" />
          </Field>
          <Toggle><input type="checkbox" checked={issuesOnly}
            onChange={event => setIssuesOnly(event.target.checked)} /> Issues only</Toggle>
          <PrimaryButton onClick={applyFilters} disabled={loading}>
            <Search size={16} /> Apply
          </PrimaryButton>
          <SecondaryButton onClick={() => load()} disabled={loading}>
            <RefreshCw size={16} /> Refresh
          </SecondaryButton>
        </FilterBar>
      </Card>

      <Card>
        <CardTitle>Batch history</CardTitle>
        <TableWrap role="region" aria-label="Scrollable Batch history" tabIndex={0}>
          <Table aria-label="Extraction Batch history">
            <thead><tr>
              <Th>Status</Th><Th>Created</Th><Th>Batch</Th><Th>Model</Th>
              <Th align="right">Items</Th><Th align="right">Input</Th>
              <Th align="right">Provider time</Th><Th align="right">Tokens</Th>
              <Th align="right">Cost</Th><Th align="right">Savings</Th><Th>Issues</Th>
            </tr></thead>
            <tbody>
              {(data?.batches || []).map(batch => (
                <SelectableRow key={batch.id} selected={batch.id === selectedBatchId}
                  issue={batch.diagnostics.length > 0}
                  onClick={() => load({ batchId: batch.id, itemPage: 1 })}>
                  <Td><Badge tone={tone(batch.status)}>{batch.status}</Badge></Td>
                  <Td>{dateTime(batch.timing.createdAt)}</Td>
                  <Td><Mono title={batch.id}>{shortId(batch.id)}</Mono><br />chunk {batch.chunkIndex}</Td>
                  <Td>{batch.model || '—'}<br /><Mono>{batch.deployment || '—'}</Mono></Td>
                  <Td align="right">{batch.completedCount}/{batch.itemCount}<br />
                    <Mono>{batch.fallbackItemCount} fallback</Mono></Td>
                  <Td align="right">{bytes(batch.inputBytes)}</Td>
                  <Td align="right">{duration(batch.timing.providerMs)}</Td>
                  <Td align="right">{batch.usage.totalTokens.toLocaleString()}</Td>
                  <Td align="right">{money(batch.cost.batchUsd)}</Td>
                  <Td align="right">{percent(batch.cost.savingsPct)}</Td>
                  <Td>{batch.diagnostics.length > 0
                    ? <Badge tone="error">{batch.diagnostics.length}</Badge> : '—'}</Td>
                </SelectableRow>
              ))}
              {data?.schemaReady && data.batches.length === 0 && (
                <tr><Td colSpan={11}>No batches match these filters.</Td></tr>
              )}
            </tbody>
          </Table>
        </TableWrap>
        <Pager>
          <span>{data ? `${data.totalCount.toLocaleString()} batch(es)` : 'Loading…'}</span>
          <ButtonRow>
            <SecondaryButton aria-label="Previous batch page" disabled={loading || page <= 1}
              onClick={() => load({ page: page - 1, batchId: null })}><ChevronLeft size={16} /></SecondaryButton>
            <span>Page {data?.page || page} of {Math.max(data?.totalPages || 0, 1)}</span>
            <SecondaryButton aria-label="Next batch page"
              disabled={loading || !data || page >= data.totalPages}
              onClick={() => load({ page: page + 1, batchId: null })}><ChevronRight size={16} /></SecondaryButton>
          </ButtonRow>
        </Pager>
      </Card>

      {selected && (
        <section aria-label="Selected Batch details">
          <Card>
            <CardTitle><Clock3 size={18} /> Selected Batch</CardTitle>
            <StatGrid>
              <Metric label="Build" value={duration(selected.timing.buildMs)} />
              <Metric label="Provider" value={duration(selected.timing.providerMs)} />
              <Metric label="Import" value={duration(selected.timing.importMs)} />
              <Metric label="Total" value={duration(selected.timing.totalMs)} />
              <Metric label="Input / cached" value={`${selected.usage.inputTokens.toLocaleString()} / ${selected.usage.cachedInputTokens.toLocaleString()}`} />
              <Metric label="Output" value={selected.usage.outputTokens.toLocaleString()} />
              <Metric label="Batch cost" value={money(selected.cost.batchUsd)} />
              <Metric label="Fallback items" value={selected.fallbackItemCount.toLocaleString()}
                tone={selected.fallbackItemCount > 0 ? 'warning' : 'neutral'} />
            </StatGrid>
            <Diagnostics values={allDiagnostics} />
            <BatchMetadata batch={selected} />
            <RawJson data={selected} label="View normalized Batch metadata" />
          </Card>

          <Card>
            <CardTitle>Batch items</CardTitle>
            <CardDescription>Failures, missing results, fallbacks, and late results are listed first.</CardDescription>
            <ItemTable items={data?.items || []} />
            <Pager>
              <span>{data?.itemTotalCount.toLocaleString()} item(s)</span>
              <ButtonRow>
                <SecondaryButton aria-label="Previous item page" disabled={loading || itemPage <= 1}
                  onClick={() => load({ itemPage: itemPage - 1 })}><ChevronLeft size={16} /></SecondaryButton>
                <span>Page {data?.itemPage || itemPage} of {Math.max(data?.itemTotalPages || 0, 1)}</span>
                <SecondaryButton aria-label="Next item page"
                  disabled={loading || !data || itemPage >= data.itemTotalPages}
                  onClick={() => load({ itemPage: itemPage + 1 })}><ChevronRight size={16} /></SecondaryButton>
              </ButtonRow>
            </Pager>
          </Card>
        </section>
      )}
    </AdminLayout>
  );
}